"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import type { EventSummary } from "@project-x/api-clients";
import { createEventSlug } from "@/utils/eventSlug";
import { createConfiguredEventsClient, fetchPublicEvents } from "@/data/events";
import { useGruvShell } from "../gruv/GruvShell";
import { useLocale, useTranslations } from "next-intl";
import {
  DEFAULT_EVENTS_FILTERS,
  type EventsFilterState,
  buildListEventsParams,
} from "@/utils/eventsFilters";
import { formatEventCardDate, getEventCardVenueParts } from "@/utils/eventCardDisplay";

export interface EventShowcaseClientProps {
  featured: EventSummary[];
  spotlight: EventSummary[];
  initialPage: number;
  pageSize: number;
  totalPages: number;
  initialFilters?: EventsFilterState;
}

const resolveLocale = (locale: string): string => (locale === "en" ? "en-US" : "es-AR");
const EVENT_CARD_IMAGE_SIZES =
  "(max-width: 767px) 100vw, (max-width: 1024px) 33vw, 280px";

const filtersEqual = (a: EventsFilterState, b: EventsFilterState): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

const IMAGE_CDN = process.env.IMAGE_CDN_URL || "";

/**
 * Returns the best card image URL for an event, preferring CARD over HERO.
 * Uses CDN URL when canonical_object_key is available.
 */
const getCardImageUrl = (event: EventSummary): string | null => {
  const asset = event.card ?? event.hero;
  if (!asset) return null;
  if (IMAGE_CDN && asset.canonical_object_key) {
    return `${IMAGE_CDN}/${asset.canonical_object_key}`;
  }
  return `/api/events/${event.id}/assets/${asset.id}/image`;
};

/**
 * A client-side component that displays a showcase of events, handles filtering, and paginates results.
 * It receives initial data from a server component but manages all subsequent interactions on the client.
 * Events are displayed as vertical ticket-like cards with hero images (300px width, 3-4 per row).
 */
export function EventShowcaseClient({
  featured,
  spotlight,
  initialPage,
  pageSize,
  totalPages,
  initialFilters = DEFAULT_EVENTS_FILTERS,
}: EventShowcaseClientProps) {
  const t = useTranslations("events");
  const locale = resolveLocale(useLocale());
  const { filters, replaceFilters } = useGruvShell();
  const [nextPage, setNextPage] = useState(initialPage);
  const [items, setItems] = useState<EventSummary[]>(spotlight);
  const [featuredEvents, setFeaturedEvents] = useState<EventSummary[]>(featured);
  const [hasMore, setHasMore] = useState(initialPage <= totalPages);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<EventsFilterState>(initialFilters);
  const [totalAvailablePages, setTotalAvailablePages] = useState(totalPages);

  // Combine featured and spotlight events into a single list
  const allEvents = useMemo(() => {
    return [...featuredEvents, ...items];
  }, [featuredEvents, items]);

  // Effect to synchronize the global shell filters with the component's initial state on mount.
  useEffect(() => {
    if (!filtersEqual(filters, initialFilters)) {
      replaceFilters(initialFilters);
    } else {
      setCurrentFilters(initialFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to refresh the event list when the global filters change.
  useEffect(() => {
    if (filtersEqual(filters, currentFilters)) {
      return;
    }

    const refresh = async () => {
      setRefreshing(true);
      setError(null);

      try {
        const client = createConfiguredEventsClient();
        const [first, second] = await Promise.all([
          fetchPublicEvents(
            buildListEventsParams(filters, { orderBy: "date", page: 1, pageSize }),
            client,
          ),
          fetchPublicEvents(
            buildListEventsParams(filters, { orderBy: "date", page: 2, pageSize }),
            client,
          ),
        ]);

        if (!first.ok || !second.ok) {
          setError(t("showcase.errors.refreshFailed"));
          setRefreshing(false);
          return;
        }

        const newFeatured = first.data.events;
        const newSpotlight = second.data.events;

        setFeaturedEvents(newFeatured);
        setItems(newSpotlight);

        const totalPagesFromApi = first.data.pagination.total_pages;
        setTotalAvailablePages(totalPagesFromApi);
        const next = 3;
        setNextPage(next);
        setHasMore(next <= totalPagesFromApi);
        setCurrentFilters(filters);

        // Hero images will be loaded by the useEffect that watches allEvents
      } catch (err) {
        setError(t("showcase.errors.refreshUnexpected"));
      } finally {
        setRefreshing(false);
      }
    };

    refresh();
  }, [filters, currentFilters, pageSize]);

  // Callback to load the next page of events, memoized for performance.
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const client = createConfiguredEventsClient();
      const response = await fetchPublicEvents(
        buildListEventsParams(currentFilters, {
          orderBy: "date",
          page: nextPage,
          pageSize,
        }),
        client,
      );

      if (!response.ok) {
        setError(response.error?.error ?? t("showcase.errors.loadMoreFailed"));
        setLoading(false);
        setHasMore(false);
        return;
      }

      const newEvents = response.data.events;
      setItems((prev) => [...prev, ...newEvents]);

      const pagination = response.data.pagination;
      const next = nextPage + 1;
      setNextPage(next);
      setTotalAvailablePages(pagination.total_pages);
      setHasMore(next <= pagination.total_pages);
    } catch (err) {
      setError(t("showcase.errors.loadMoreUnexpected"));
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, nextPage, pageSize, currentFilters]);

  return (
    <section className="gruv-showcase" aria-label={t("showcase.aria.section")}>
      {refreshing ? <div className="gruv-showcase__loading">{t("showcase.refreshing")}</div> : null}

      {/* Single grid showing all events as vertical ticket-like cards */}
      {allEvents.length === 0 && !refreshing ? (
        <p className="gruv-showcase__end">{t("showcase.endEmpty")}</p>
      ) : allEvents.length === 0 && refreshing ? (
        <div className="gruv-showcase__loading">{t("showcase.loading")}</div>
      ) : (
        <div className="gruv-showcase__grid gruv-showcase__grid--tickets">
          {allEvents.map((event, index) => {
            const slug = createEventSlug(event);
            const cardImageUrl = getCardImageUrl(event);
            const cardDate = formatEventCardDate(event.starts_at, locale);
            const venueParts = getEventCardVenueParts(event);
            const venueAria = venueParts
              ? [venueParts.name, venueParts.region].filter(Boolean).join(", ")
              : "";

            return (
              <Link
                key={`${event.id}-${event.starts_at}`}
                href={`/events/${slug}`}
                prefetch={false}
                className="gruv-card gruv-card--ticket"
                aria-label={t("showcase.aria.card", {
                  name: event.name,
                  date: cardDate,
                })}
              >
                <div
                  className="gruv-card__image-wrap"
                  aria-hidden="true"
                >
                  {cardImageUrl ? (
                    <Image
                      src={cardImageUrl}
                      alt=""
                      fill
                      unoptimized
                      sizes={EVENT_CARD_IMAGE_SIZES}
                      priority={index < 2}
                      loading={index < 2 ? "eager" : "lazy"}
                      className="gruv-card__image"
                    />
                  ) : null}
                </div>
                <div className="gruv-card__content">
                  <div className="gruv-card__header">
                    <span className="gruv-card__date">{cardDate}</span>
                  </div>
                  <h3 className="gruv-card__title">{event.name}</h3>
                  {venueParts ? (
                    <div className="gruv-card__venue" aria-label={venueAria}>
                      <p className="gruv-card__venue-row">
                        <span className="gruv-card__venue-marker" aria-hidden="true">
                          📍
                        </span>
                        <span className="gruv-card__venue-name">
                          {venueParts.name || venueParts.region}
                        </span>
                      </p>
                      {venueParts.name && venueParts.region ? (
                        <p className="gruv-card__venue-row">
                          <span className="gruv-card__venue-marker" aria-hidden="true">
                            •
                          </span>
                          <span className="gruv-card__venue-region">{venueParts.region}</span>
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="gruv-showcase__actions">
        {error ? <p className="gruv-showcase__error">{error}</p> : null}
        {hasMore ? (
          <button type="button" className="gruv-showcase__load" onClick={loadMore} disabled={loading}>
            {loading ? t("showcase.loadMoreLoading") : t("showcase.loadMore")}
          </button>
        ) : allEvents.length > 0 ? (
          <p className="gruv-showcase__end">
            {totalAvailablePages > 1
              ? t("showcase.endWithFilters")
              : t("showcase.endEmpty")}
          </p>
        ) : null}
      </div>
    </section>
  );
}

